import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, useMapEvents } from "../../lib/react-leaflet-lite";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { STOP_STATUS, clusterStops, clusterTone } from "./clustering";
import { formatElapsed } from "../../services/locationSocketService";
import { useBasemapTile } from "../../config/basemaps";
import { createCircleMarker } from "../../utils/mapHelpers";
import { useDriverPlannedRoute } from "../../views/adminView/actividad/useDriverPlannedRoute";
import useOperationFraming from "../../hooks/useOperationFraming";
import InitialFraming from "../../components/map/InitialFraming";

// Encuadre de arranque del <MapContainer>. Es provisional por diseño: el mapa se
// crea una sola vez y `center`/`zoom` son inertes después (ver
// lib/react-leaflet-lite.jsx), así que el encuadre real lo aplica
// <InitialFraming> de forma imperativa en cuanto sabe dónde opera el cliente.
// Se mantiene un valor concreto porque L.map() exige uno para inicializar.
const BOOTSTRAP_VIEW = { center: [-33.4489, -70.6693], zoom: 11 };

// ── Icono de parada individual: punto coloreado por estado ──────────────
// Migrado a createCircleMarker (audit #8). Tamaño/borde/sombra idénticos.
function stopIcon(key) {
  const color = STOP_STATUS[key]?.color || STOP_STATUS.pending.color;
  return createCircleMarker({
    size: 13,
    color,
    borderColor: '#fff',
    borderWidth: 2,
    shadow: '0 1px 3px rgba(0,0,0,.35)',
    className: 'lt-stop-icon',
  });
}

// ── Icono de cluster: círculo con el número de pedidos y anillo de tono ──
// TODO: extraer a createCircleMarker — tiene caso especial (fondo
// var(--surface-primary), border-color/text-color = tono, clase CSS .lt-cluster
// con :hover scale(1.1) en liveTracking.scss). La estructura no encaja con
// createCircleMarker sin diluir el contrato del helper.
function clusterIcon(cluster) {
  const tone = clusterTone(cluster.counts);
  const n = cluster.count;
  const size = n < 10 ? 24 : n < 50 ? 28 : n < 200 ? 33 : 38;
  return L.divIcon({
    className: "lt-cluster-icon",
    html: `<div class="lt-cluster" style="width:${size}px;height:${size}px;border-color:${tone};color:${tone};">
      <span>${n}</span>
    </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

// ── Icono de repartidor: auto pequeño, color por estado GPS ──────────────
// Badge circular compacto con la silueta de un auto (nombre en el popup al
// hacer click). Migrado a createCircleMarker con glyphFont:'svg'.
// El hover :scale(1.08) viene del className `lt-car-icon` (definido en
// liveTracking.scss vía el selector externo del divIcon).
const CAR_SVG = (size) => `<svg width="${Math.round(size * 0.6)}" height="${Math.round(size * 0.6)}" viewBox="0 0 24 24" fill="#fff" aria-hidden="true"><path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/></svg>`;

function driverIcon(status, isSelected) {
  const colors = { online: "#22c55e", inactive: "#f59e0b", offline: "#9ca3af", "no-gps": "#9ca3af" };
  const color = isSelected ? "#ef4444" : colors[status] || colors.offline;
  const size = isSelected ? 34 : 28;
  return createCircleMarker({
    size,
    color,
    borderColor: '#fff',
    borderWidth: isSelected ? 3 : 2,
    glyph: CAR_SVG(size),
    glyphFont: 'svg',
    shadow: isSelected
      ? '0 3px 10px rgba(0,0,0,0.45)'
      : '0 2px 6px rgba(0,0,0,0.35)',
    className: `lt-car-icon${isSelected ? ' lt-car-icon--selected' : ''}`,
  });
}

// ── Capa de paradas con clustering reactivo a zoom/encuadre ──────────────
function StopClusterLayer({ stops }) {
  const { t } = useTranslation('tracking');
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  const [bounds, setBounds] = useState(() => map.getBounds());

  useMapEvents({
    zoomend: () => {
      setZoom(map.getZoom());
      setBounds(map.getBounds());
    },
    moveend: () => setBounds(map.getBounds()),
  });

  const clusters = useMemo(() => {
    const padded = bounds.pad(0.3);
    const visible = stops.filter((s) => padded.contains([s.lat, s.lng]));
    return clusterStops(visible, zoom);
  }, [stops, zoom, bounds]);

  return clusters.map((c, i) => {
    if (c.count === 1) {
      const s = c.items[0];
      const st = STOP_STATUS[s.key];
      return (
        <Marker key={s.uid} position={[s.lat, s.lng]} icon={stopIcon(s.key)}>
          <Popup>
            <div className="lt-popup">
              <span className="lt-popup__dot" style={{ background: st.color }} />
              <strong>#{s.followingNumber || s.id?.slice(-6)}</strong>
              <span className="lt-popup__status" style={{ color: st.color }}>{t(st.label)}</span>
              {s.driverName && <span className="lt-popup__meta">{s.driverName}</span>}
              {s.address && <span className="lt-popup__meta">{s.address}</span>}
            </div>
          </Popup>
        </Marker>
      );
    }
    return (
      <Marker
        key={`c${i}-${c.lat.toFixed(4)}-${c.lng.toFixed(4)}`}
        position={[c.lat, c.lng]}
        icon={clusterIcon(c)}
        eventHandlers={{
          click: () => map.flyTo([c.lat, c.lng], Math.min(zoom + 2, 18), { duration: 0.5 }),
        }}
      />
    );
  });
}

// ── Vuela al objetivo (repartidor seleccionado) cuando cambia el token ───
function FlyController({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    map.flyTo([target.lat, target.lng], target.zoom ?? 15, { duration: 0.7 });
  }, [target, map]);
  return null;
}

// ── Controles de zoom +/- ────────────────────────────────────────────────
function ZoomControls() {
  const { t } = useTranslation('tracking');
  const map = useMap();
  return (
    <div className="lt-zoom">
      <button className="lt-zoom__btn" onClick={() => map.zoomIn()} title={t('Acercar')} aria-label={t('Acercar')}>+</button>
      <button className="lt-zoom__btn" onClick={() => map.zoomOut()} title={t('Alejar')} aria-label={t('Alejar')}>−</button>
    </div>
  );
}

/**
 * Ruta que el repartidor seleccionado TODAVÍA tiene por recorrer, en el orden real que va a
 * seguir (lo publica su teléfono; ver `optimizedRoute.source === 'device'`).
 *
 * Solo del seleccionado: dibujar la traza de todos los repartidores a la vez convierte el mapa
 * en un ovillo y deja de responder la pregunta que el operador está haciendo.
 *
 * Va DEBAJO de los marcadores (`back`) para no tapar las paradas ni los autos, y en trazo
 * discontinuo para que se lea distinto del recorrido ya hecho.
 */
function PlannedRouteLayer({ driverId }) {
  const { plannedCoords } = useDriverPlannedRoute(driverId);
  if (!plannedCoords || plannedCoords.length < 2) return null;
  return (
    <Polyline
      positions={plannedCoords}
      color="#2563eb"
      weight={4}
      opacity={0.75}
      dashArray="8 10"
      back
    />
  );
}

export default function TrackingMap({ isDark, stops, drivers, showDrivers = true, selectedDriverId, onSelectDriver, focusTarget }) {
  // Tile del basemap activo (fuente única configurable desde Configuración).
  const tile = useBasemapTile(isDark);
  // Dónde opera el cliente (instalaciones de /admin/settings?tab=locations).
  // `null` mientras no se sabe: no encuadrar es mejor que encuadrar mal.
  const frame = useOperationFraming();

  // Todo lo que el operador tiene que poder ver del día. Se incluyen los
  // repartidores además de las paradas porque un día recién empezado puede tener
  // GPS vivo y ninguna parada geolocalizada todavía.
  const framePoints = useMemo(() => {
    const points = stops.map((s) => [s.lat, s.lng]);
    if (showDrivers) {
      drivers.forEach((d) => {
        const { latitude, longitude } = d.location || {};
        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
          points.push([latitude, longitude]);
        }
      });
    }
    return points;
  }, [stops, drivers, showDrivers]);

  return (
    <MapContainer
      center={BOOTSTRAP_VIEW.center}
      zoom={BOOTSTRAP_VIEW.zoom}
      zoomControl={false}
      style={{ position: "absolute", inset: 0, height: "100%", width: "100%", zIndex: 1 }}
    >
      <TileLayer
        key={tile.url}
        url={tile.url}
        attribution={tile.attribution}
        subdomains={tile.subdomains}
        maxZoom={tile.maxZoom}
      />

      <ZoomControls />
      {/* Cede el encuadre en cuanto se enfoca un repartidor: a partir de ahí manda
          FlyController y reencuadrar sería pelearle la cámara. Un ?driver= de la
          URL sin foco todavía (o ausente en los datos del día) NO lo desactiva:
          así el mapa encuadra el día en vez de quedarse en la vista por defecto. */}
      <InitialFraming
        frame={frame}
        dataPoints={framePoints}
        enabled={!focusTarget}
        obstructionSelector=".lt-panel"
      />
      <FlyController target={focusTarget} />

      <StopClusterLayer stops={stops} />

      {selectedDriverId && <PlannedRouteLayer driverId={selectedDriverId} />}

      {showDrivers && drivers.map((d) => (
        <Marker
          key={d.driverId}
          position={[d.location.latitude, d.location.longitude]}
          icon={driverIcon(d.status, d.driverId === selectedDriverId)}
          zIndexOffset={d.driverId === selectedDriverId ? 1000 : 500}
          eventHandlers={{ click: () => onSelectDriver(d.driverId) }}
        >
          <Popup>
            <div className="lt-popup">
              <strong>{d.name}</strong>
              {d.location.speed != null && (
                <span className="lt-popup__meta">{(d.location.speed * 3.6).toFixed(0)} km/h</span>
              )}
              <span className="lt-popup__meta">{formatElapsed(d.location.timestamp)}</span>
            </div>
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
}
